import { useEffect, useState } from "react";
import { Wine } from "lucide-react";
import { useLanguage, pick } from "@/i18n";
import { api } from "@/api";
import { useSEO } from "@/hooks/useSEO";
import ExperienceCard from "@/components/ExperienceCard";
import SectionHeader from "@/components/SectionHeader";

const HERO_IMG = "https://images.unsplash.com/photo-1620475676913-9497df261cc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwZmluZSUyMGRpbmluZyUyMHdpbmV8ZW58MHx8fHwxNzg3OTAwOTk0fDA&ixlib=rb-4.1.0&q=85";

const TERRITORIES = ["Campi Flegrei", "Vesuvio", "Irpinia", "Sannio", "Cilento", "Costiera", "Casertano", "Napoli"];

export default function Taste() {
  const { lang, t } = useLanguage();
  useSEO(t("seo_taste_title"), t("seo_taste_desc"));
  const [experiences, setExperiences] = useState([]);
  const [addons, setAddons] = useState([]);

  useEffect(() => {
    api.experiences().then((data) => {
      setExperiences(data.filter((e) => e.categories.some((c) => ["food", "wine"].includes(c))));
    }).catch(() => {});
    api.addons().then(setAddons).catch(() => {});
  }, []);

  return (
    <div data-testid="taste-page">
      <section className="relative h-[65vh] min-h-[450px] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Taste Campania" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-16 w-full">
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/80 mb-4">Food & Wine</p>
          <h1 className="font-serif text-5xl md:text-7xl text-ivory tracking-tight" data-testid="taste-hero-title">
            Taste Campania
          </h1>
          <p className="mt-4 font-serif italic text-xl md:text-2xl text-ivory/85">{t("taste_page_sub")}</p>
        </div>
      </section>

      {/* MAKE IT TASTE BETTER */}
      <section className="py-24 bg-sand" data-testid="make-taste-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-3">
            <Wine size={20} strokeWidth={1.25} className="text-terracotta" />
            <h2 className="font-serif text-4xl text-ink">{t("make_taste")}</h2>
          </div>
          <p className="text-ink/60 max-w-2xl leading-relaxed">{t("make_taste_sub")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {addons.map((a) => (
              <div key={a.slug} className="bg-ivory border border-stone2/40 p-6" data-testid={`taste-addon-${a.slug}`}>
                <div className="overflow-hidden aspect-[4/3] mb-4 img-zoom">
                  <img src={a.image} alt={pick(a.name, lang)} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <h4 className="font-medium text-ink">{pick(a.name, lang)}</h4>
                <p className="mt-1 text-xs text-ink/50 leading-relaxed">{pick(a.description, lang)}</p>
                <p className="mt-3 text-sm text-medblue">
                  {t("on_request")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TASTE EXPERIENCES */}
      <section className="py-24" data-testid="taste-experiences-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader overline="Food & Wine" title={t("taste_title")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14 mt-14">
            {experiences.map((exp) => (
              <ExperienceCard key={exp.slug} exp={exp} />
            ))}
          </div>
        </div>
      </section>

      {/* TERRITORIES */}
      <section className="py-20 bg-sand" data-testid="territories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.3em] text-medblue mb-8">{t("territories_title")}</h2>
          <div className="flex flex-wrap gap-3">
            {TERRITORIES.map((terr) => (
              <span key={terr} data-testid={`territory-${terr.toLowerCase()}`} className="border border-stone2 bg-ivory px-5 py-2.5 text-sm text-ink/70">
                {terr}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
